/**
 * The dashboard query — what every analytics widget is asked for.
 *
 * Serializable by construction (ISO dates, sorted string arrays) because it is
 * part of an RTK Query cache key. Build it with `canonicalQuery()` from
 * `@/features/filters`, never by hand: that function sorts selections and
 * prunes them to the chips the current page renders (docs/design/rules.md §0 —
 * "a module never filters by a chip it doesn't render").
 */
import type { IsoDate } from '@/lib/analytics/dates';

export type DimensionKey = 'brand' | 'channel' | 'intent' | 'topic' | 'conversation' | 'sentiment';

export const DIMENSION_KEYS = [
  'brand',
  'channel',
  'intent',
  'topic',
  'conversation',
  'sentiment',
] as const satisfies readonly DimensionKey[];

export type FilterOption = { value: string; label: string };

/** Every chip's options, in the order the server ranks them (drives series colour). */
export type FilterOptions = Readonly<Record<DimensionKey, readonly FilterOption[]>>;

/** Which filters bar a page draws — Competitors adds the Brands chip. */
export type FiltersType = 'default' | 'competitors';

/**
 * A picker value. `start`/`end` may each be open: an open start means "from the
 * beginning of the data", an open end "to today"; both open means no range
 * (the prototype's "no range selected" state, which shows every card empty).
 */
export type DateRangeValue = { start: IsoDate | null; end: IsoDate | null };

/**
 * Absent key → the dimension is unconstrained (all options, or not rendered).
 * Present key → only these values; `[]` matches nothing ("an emptied chip
 * matches nothing", docs/design/rules.md).
 */
export type Selections = Partial<Record<DimensionKey, readonly string[]>>;

export type DashboardQuery = {
  range: DateRangeValue;
  /** `null` when comparison is off. */
  compare: DateRangeValue | null;
  selections: Selections;
  /**
   * Set on a custom report: the source may scope the widget to the posts the
   * report's campaign linked (all posts when it has none).
   */
  dashboardId?: string;
};
